import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import type { ReceivableInvoicesUploadAPI } from 'receivable-invoices-js/react/api/models/receivable-invoices-upload';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface QueryResponse {
  data: {
    id: string;
    type: string;
    attributes: ReceivableInvoicesUploadAPI;
  };
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/uploads/shared`;

export function fetchReceivableInvoicesUploadKey(id: string): QueryKey {
  return ['receivable-invoices-upload', id] as QueryKey;
}

export async function fetchReceivableInvoicesUpload(id: string, customOptions?: CustomOptions) {
  const { onError } = customOptions || {};

  try {
    const response = await qontoClient<QueryResponse>(`${URL}/${id}`);

    return camelizeKeys({
      ...response.data.attributes,
      id: response.data.id,
    }) as ReceivableInvoicesUploadAPI;
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchReceivableInvoicesUploadResponse = Awaited<
  ReturnType<typeof fetchReceivableInvoicesUpload>
>;

export function useFetchReceivableInvoicesUpload(
  id: string,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchReceivableInvoicesUploadResponse>>
) {
  return useQuery({
    queryKey: fetchReceivableInvoicesUploadKey(id),
    queryFn: () => fetchReceivableInvoicesUpload(id, { ...customOptions }),
    ...queryOptions,
  });
}
